/**
 * PortalEmptyState — explains to a student/parent WHY a section is empty,
 * instead of the blunt "No timetable available" / "No classes assigned"
 * we used before. The hint text changes based on whether the student is
 * enrolled in a class. The component is read-only so it works inside the
 * fee-gate too.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Info, GraduationCap, Loader2 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalEmptyState({ title = 'Nothing here yet', kind = 'generic' }) {
  const { data: me, isLoading } = useQuery({
    queryKey: ['portal-me-empty-state'],
    queryFn: async () => {
      // Match the same token resolution the portal uses: try sessionStorage
      // first (preview / view-as) then fall back to the regular bearer.
      const preview = (typeof sessionStorage !== 'undefined') &&
        sessionStorage.getItem('school_preview_token');
      const t = preview || getToken();
      const res = await fetch(`${API_URL}/api/school/portal/me`, {
        headers: { 'Authorization': `Bearer ${t || ''}` },
      });
      return res.ok ? res.json() : {};
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const profile = me?.profile || me || {};
  const noClass = !profile.class_id;

  // Each `kind` is matched to a friendly explanation. We never reveal
  // anything beyond what the student already knows about themselves.
  const reasons = {
    timetable: noClass
      ? "You're not yet assigned to a class. Once your school assigns you to one, your timetable will appear here automatically."
      : `You're enrolled in ${profile.class_name || 'a class'}, but no timetable has been published for it yet. Your school will publish lessons soon.`,
    classes: noClass
      ? "You're not yet enrolled in any class. Once enrolment is completed, your classes will show here."
      : `You're enrolled in ${profile.class_name || 'a class'}, but it hasn't been linked to subjects yet.`,
    grades: noClass
      ? "Grades will appear once you've been assigned to a class and graded for an assessment."
      : 'No grades yet — your first results will appear here as soon as a teacher publishes them.',
    announcements:
      "No announcements yet. When your school posts notices for your class, you'll see them here.",
    lessons: noClass
      ? "No lessons yet — you'll see them after you're enrolled in a class."
      : 'No lessons published for your class yet.',
    reading_list:
      "No books assigned yet. Your teachers will add required reading here when they're ready.",
    generic: 'Nothing here yet.',
  };

  const Icon = noClass ? GraduationCap : Info;
  const tone = noClass ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50/60';
  const headTone = noClass ? 'text-amber-700' : 'text-slate-700';

  return (
    <Card className={`border-dashed ${tone}`} data-testid={`empty-state-${kind}`}>
      <CardContent className="py-10 text-center max-w-md mx-auto">
        <Icon className={`h-7 w-7 mx-auto mb-2 ${headTone}`} />
        <h3 className={`font-medium ${headTone}`}>{title}</h3>
        <p className="text-sm text-slate-600 mt-1">{reasons[kind] || reasons.generic}</p>
      </CardContent>
    </Card>
  );
}
